import React from 'react';
import dayjs from 'dayjs';
import {
  SessionEventData,
  ContentData,
  EventFieldData,
  FieldValueData,
  EventActionData,
  RelatedEventData,
  EventField,
  EventAction,
  RelatedEvent,
  EventCardProps,
} from '../types/sessionTimeline.types';
import { getSessionIcon, getFieldIcon } from './iconMapping';

/**
 * FieldValueData를 문자열로 변환
 */
function formatFieldValue(fieldValue: FieldValueData): string {
  const { type, value, format } = fieldValue;

  switch (type) {
    case 'date': {
      const date = dayjs(value);
      if (!date.isValid()) return String(value);
      return format ? date.format(format) : date.format('YYYY-MM-DD');
    }
    case 'datetime': {
      const date = dayjs(value);
      if (!date.isValid()) return String(value);
      return format ? date.format(format) : date.toISOString();
    }
    case 'number': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      if (format === 'integer') {
        return Math.round(num).toString();
      }
      return num.toString();
    }
    case 'currency': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      const locale = format || 'ko-KR';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'KRW',
      }).format(num);
    }
    case 'text':
    default:
      return String(value);
  }
}

/**
 * ContentData를 ReactNode로 변환
 */
function transformContent(content: ContentData): React.ReactNode {
  switch (content.type) {
    case 'text':
      return content.value;
    case 'html':
      // HTML은 dangerouslySetInnerHTML로 렌더링 (주의 필요)
      return React.createElement('div', {
        dangerouslySetInnerHTML: { __html: content.value },
      });
    case 'component':
      // 컴포넌트 타입은 클라이언트에서 처리해야 함
      // 여기서는 기본적으로 텍스트로 반환
      console.warn(
        `Component type "${content.componentType}" is not supported. Rendering as text.`
      );
      return content.value;
    default:
      return content.value;
  }
}

/**
 * EventFieldData를 EventField로 변환
 */
export function transformField(
  fieldData: EventFieldData,
  fieldIcons?: Readonly<Record<string, React.ReactNode>>
): EventField {
  const value =
    typeof fieldData.value === 'string' ? fieldData.value : formatFieldValue(fieldData.value);

  let icon: React.ReactNode | undefined;
  if (fieldData.iconType) {
    // fieldIcons에서 먼저 찾고, 없으면 iconMapping에서 찾기
    if (fieldIcons && fieldIcons[fieldData.iconType]) {
      icon = fieldIcons[fieldData.iconType];
    } else {
      const IconComponent = getFieldIcon(fieldData.iconType);
      if (IconComponent) {
        icon = React.createElement(IconComponent);
      }
    }
  }

  return {
    label: fieldData.label,
    value,
    icon,
    copyable: fieldData.copyable,
    multiline: fieldData.multiline,
    markdown: fieldData.markdown,
  };
}

/**
 * EventActionData를 EventAction으로 변환
 */
function transformAction(actionData: EventActionData): EventAction {
  return {
    id: actionData.id,
    label: actionData.label,
    color: actionData.color,
    default: actionData.default,
    // onClick은 클라이언트에서 추가해야 함
  };
}

/**
 * RelatedEventData를 RelatedEvent로 변환
 */
function transformRelatedEvent(relatedEventData: RelatedEventData): RelatedEvent {
  return {
    id: relatedEventData.id,
    title: relatedEventData.title,
    occurredAt: relatedEventData.occurredAt,
  };
}

/**
 * SessionEventData를 EventCardProps로 변환
 *
 * @param data JSON 데이터
 * @param options 변환 옵션
 * @returns EventCardProps
 */
export function transformSessionEventData(
  data: SessionEventData,
  options?: {
    fieldIcons?: Readonly<Record<string, React.ReactNode>>;
    onActionClick?: (actionId: string, sessionId: string) => void;
    onRelatedEventsClick?: (sessionId: string) => void;
    onExpandStateChange?: (sessionId: string, state: 'collapsed' | 'content' | 'timeline') => void;
  }
): EventCardProps {
  const {
    sessionId,
    title,
    occurredAt,
    sessionIconType,
    index,
    content,
    fields,
    actions,
    relatedEvents,
    relatedEventsCount,
    collapsible,
    defaultExpandedState,
  } = data;

  // 세션 아이콘 변환
  let sessionIcon: React.ReactNode | undefined;
  if (sessionIconType) {
    const IconComponent = getSessionIcon(sessionIconType);
    if (IconComponent) {
      sessionIcon = React.createElement(IconComponent);
    }
  }

  // 컨텐츠 변환
  let contentNode: React.ReactNode | ContentData | undefined;
  if (content) {
    contentNode = transformContent(content);
  }

  // 필드 변환
  const transformedFields = fields?.map((field) => transformField(field, options?.fieldIcons));

  // 액션 변환
  const transformedActions = actions?.map(transformAction);

  // 관련 이벤트 변환
  const transformedRelatedEvents = relatedEvents?.map(transformRelatedEvent);

  return {
    sessionId,
    title,
    occurredAt,
    sessionIconType,
    sessionIcon,
    index,
    content: contentNode,
    fields: transformedFields,
    actions: transformedActions,
    relatedEvents: transformedRelatedEvents,
    relatedEventsCount,
    collapsible,
    defaultExpandedState,
    onActionClick: options?.onActionClick,
    onRelatedEventsClick: options?.onRelatedEventsClick,
    onExpandStateChange: options?.onExpandStateChange,
    fieldIcons: options?.fieldIcons,
  };
}
